import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { INTENTS, VIBES, ACTIVITY_TYPES, COMMUNITIES } from "@/lib/mockData";
import { toast } from "sonner";
import { apiUsers } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const STEPS = ["Suburb", "Interests", "Communities", "Default intents", "Default vibes"];

const Onboarding = () => {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [suburb, setSuburb] = useState(user?.suburb || "Burleigh Heads");
  const [interests, setInterests] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [intents, setIntents] = useState([]);
  const [vibes, setVibes] = useState([]);

  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const toggleLimited = (arr, v, max) => {
    if (arr.includes(v)) return arr.filter((x) => x !== v);
    if (arr.length >= max) return arr;
    return [...arr, v];
  };

  const next = async () => {
    if (step < STEPS.length - 1) return setStep(step + 1);
    try {
      if (user) {
        await apiUsers.updateMe({
          suburb, interests, communities,
          default_intent: intents.length ? intents : ["casual_hang"],
          default_vibe: vibes.length ? vibes : ["social"],
        });
        await refresh();
      }
      toast.success("Profile set up — let's go");
      nav("/feed");
    } catch {
      toast.error("Couldn't save — try again");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] px-4 py-8" data-testid="onboarding-page">
      <div className="max-w-md mx-auto">
        {/* Progress */}
        <div className="flex gap-1.5 mb-6">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full brutal-border ${i <= step ? "bg-[#FF6A2C]" : "bg-white"}`}
            />
          ))}
        </div>
        <div className="label-mono text-[#555555] mb-2">Step {step + 1} of {STEPS.length}</div>
        <h1 className="font-heading font-black text-3xl mb-6">{STEPS[step]}</h1>

        <div className="brutal-card p-6">
          {step === 0 && (
            <div>
              <label className="label-mono block mb-2">Where do you usually grab stuff?</label>
              <input
                value={suburb}
                onChange={(e) => setSuburb(e.target.value)}
                placeholder="e.g. Surry Hills"
                className="brutal-input"
                data-testid="onboarding-suburb"
              />
              <p className="text-xs text-[#555555] mt-3">We'll use this to surface nearby activities.</p>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="text-sm text-[#555555] mb-4">Pick a few — you can change these later.</p>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_TYPES.map((t) => {
                  const on = interests.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => setInterests(toggle(interests, t.id))}
                      className={`brutal-border rounded-full px-3 py-2 font-bold text-sm ${
                        on ? "bg-[#FF6A2C] text-white" : "bg-white"
                      }`}
                      data-testid={`onboarding-interest-${t.id}`}
                    >
                      {t.emoji} {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-sm text-[#555555] mb-4">Stand-out groups to find your people. Pick any that vibe with you.</p>
              <div className="flex flex-wrap gap-2">
                {COMMUNITIES.map((c) => {
                  const on = communities.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setCommunities(toggle(communities, c.id))}
                      className={`brutal-border rounded-full px-3 py-2 font-bold text-sm ${on ? "ring-4 ring-[#FF6A2C]/40" : "opacity-80"}`}
                      style={{ background: c.color }}
                      data-testid={`onboarding-community-${c.id}`}
                    >
                      {c.emoji} {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2">
              <p className="text-sm text-[#555555] mb-2">Pick up to 3.</p>
              {INTENTS.map((i) => {
                const on = intents.includes(i.id);
                const atMax = intents.length >= 3 && !on;
                return (
                  <button
                    key={i.id}
                    onClick={() => setIntents(toggleLimited(intents, i.id, 3))}
                    disabled={atMax}
                    className={`w-full text-left brutal-border rounded-lg p-3 flex items-center gap-3 ${
                      on ? "bg-[#0A0A0A] text-white" : "bg-white"
                    } ${atMax ? "opacity-30 cursor-not-allowed" : ""}`}
                    data-testid={`onboarding-intent-${i.id}`}
                  >
                    <span className="text-xl">{i.emoji}</span>
                    <span className="font-bold">{i.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 4 && (
            <div>
              <p className="text-sm text-[#555555] mb-3">Pick up to 4.</p>
              <div className="grid grid-cols-2 gap-2">
                {VIBES.map((v) => {
                  const on = vibes.includes(v.id);
                  const atMax = vibes.length >= 4 && !on;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setVibes(toggleLimited(vibes, v.id, 4))}
                      disabled={atMax}
                      className={`brutal-border rounded-lg p-4 text-center font-bold ${
                        on ? "ring-4 ring-[#FF6A2C]/40" : ""
                      } ${atMax ? "opacity-30 cursor-not-allowed" : ""}`}
                      style={{ background: v.color }}
                      data-testid={`onboarding-vibe-${v.id}`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="brutal-btn bg-white px-5 py-3" data-testid="onboarding-back">
              Back
            </button>
          )}
          <button onClick={next} className="flex-1 brutal-btn bg-[#FF6A2C] text-white py-3" data-testid="onboarding-next">
            {step < STEPS.length - 1 ? "Continue" : "Finish"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
